import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { formatOfferDate, formatOfferDimensions } from '@core/offers/offer-format';
import { OffersService } from '@core/offers/offers.service';
import { ProposalService } from '@core/proposals/proposal.service';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-dialog-select-offer',
  templateUrl: './dialog-select-offer.component.html',
  styleUrls: ['./dialog-select-offer.component.scss'],
})
export class DialogSelectOfferComponent implements OnInit {
  proposal: any;
  offers: any[] = [];
  readonly formatOfferDimensions = formatOfferDimensions;
  readonly formatOfferDate = formatOfferDate;
  searchTerm = '';
  saving = false;
  errorMessage: string | null = null;

  private _allOffers: any[] = [];
  // The proposal's current products, read live from ProposalService rather than
  // from `this.proposal` (a snapshot taken when the page loaded, which does not
  // reflect offers added earlier in this same session before a reload).
  private _existingProducts: any[] = [];

  constructor(
    private _dialogRef: MatDialogRef<DialogSelectOfferComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _service: OffersService,
    private _proposalService: ProposalService,
    private _translocoService: TranslocoService,
  ) {
    this.proposal = data.proposal;
  }

  ngOnInit(): void {
    this._proposalService.products$.subscribe((products) => {
      this._existingProducts = products ?? [];
      this.offers = this.filterByCurrency(this._allOffers);
    });
    this._service.offers$.subscribe((offers) => {
      this._allOffers = offers ?? [];
      this.offers = this.filterByCurrency(this._allOffers);
    });
    this.findAll();
  }

  /**
   * A proposal can only store a single currencyId — there is no currency
   * conversion anywhere in this system — so every offer already added to it
   * must share one currency. This reads it off the first existing product's
   * "main currency" Pricing row.
   */
  private offerCurrencyId(offer: any): number | null {
    const pricing = offer?.Pricings?.find((p: any) => p.isMainCurrency);
    const id = pricing?.currencyId ?? pricing?.currency?.id;
    return id != null ? +id : null;
  }

  get proposalCurrencyId(): number | null {
    for (const product of this._existingProducts) {
      const id = this.offerCurrencyId(product?.product);
      if (id != null) {
        return id;
      }
    }
    return null;
  }

  get proposalCurrencyName(): string | null {
    for (const product of this._existingProducts) {
      const pricing = product?.product?.Pricings?.find((p: any) => p.isMainCurrency);
      if (pricing?.currency?.name) {
        return pricing.currency.name;
      }
    }
    return null;
  }

  /**
   * Hides offers priced in a different currency than the proposal's existing
   * offers — cleaner than letting the user pick one and then rejecting it.
   * Offers with no resolvable currency are left visible (defensive: better to
   * show them and let saveAndClose()'s guard catch a real mismatch than to
   * silently hide offers due to an unexpected data shape).
   */
  private filterByCurrency(offers: any[]): any[] {
    const currencyId = this.proposalCurrencyId;
    if (currencyId == null) {
      return offers;
    }
    return offers.filter((offer) => {
      const offerCurrencyId = this.offerCurrencyId(offer);
      return offerCurrencyId == null || offerCurrencyId === currencyId;
    });
  }

  findAll(): void {
    this._service
      .search({ term: this.searchTerm })
      .subscribe();
  }

  close(): void {
    this._dialogRef.close();
  }

  price(offer: any): number {
    return offer?.Pricings?.find((p: any) => p.isMainCurrency)?.value ?? 0;
  }

  priceCurrency(offer: any): string {
    return offer?.Pricings?.find((p: any) => p.isMainCurrency)?.currency?.name ?? 'EUR';
  }

  saveAndClose(offer: any): void {
    // Belt-and-suspenders: filterByCurrency() should already keep a mismatched
    // offer out of the list the user picks from, but this guard is what
    // actually prevents the mixed-currency proposal if that filtering was ever
    // bypassed (e.g. a stale offers$ list, or a caller invoking this directly).
    const currencyId = this.proposalCurrencyId;
    const offerCurrencyId = this.offerCurrencyId(offer);
    if (currencyId != null && offerCurrencyId != null && offerCurrencyId !== currencyId) {
      this.errorMessage = this._translocoService.translate(
        'proposals.dialogs.selectOffer.currencyMismatchError',
        { currency: this.proposalCurrencyName },
      );
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    this._proposalService.addProductToProposal(this.proposal.id, { product: offer }).subscribe({
      next: () => {
        this._proposalService.getByIdToGetProducts(this.proposal.id).subscribe({
          next: (res) => {
            this.saving = false;
            if (res?.ProductOffer2Proposal) {
              res.ProductOffer2Proposal.forEach((element: any) => {
                this._proposalService.products = element;
              });
            }
            this._dialogRef.close(true);
          },
          error: () => {
            this.saving = false;
            this.errorMessage = this._translocoService.translate('proposals.dialogs.selectOffer.refreshError');
          },
        });
      },
      error: () => {
        this.saving = false;
        this.errorMessage = this._translocoService.translate('proposals.dialogs.selectOffer.saveError');
      },
    });
  }
}
